import { useState, useEffect, useCallback } from 'react';
import { fetchBitbucketBotToken } from '@/apis/prReviewsConfig';

/**
 * Shared hook that checks whether a Bitbucket org already has an
 * App Password configured and exposes modal-open state.
 *
 * @param orgUUID  – the organisation UUID
 * @param isBitbucket – whether the current user/session is a Bitbucket provider
 * @param options.openModalWhenMissing – if true, automatically open the modal
 *   when no token is found on mount (default: false)
 */
export function useBitbucketOrgToken(
  orgUUID: string,
  isBitbucket: boolean,
  options?: { openModalWhenMissing?: boolean }
) {
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isBitbucket || !orgUUID) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchBitbucketBotToken({ OrgUUID: orgUUID });
        if (cancelled) return;
        const tokenExists = res.HasToken ?? false;
        setHasToken(tokenExists);
        if (!tokenExists && options?.openModalWhenMissing) {
          setShowModal(true);
        }
      } catch {
        if (!cancelled) setHasToken(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isBitbucket, orgUUID, options?.openModalWhenMissing]);

  /** Call after the token modal succeeds to update local state. */
  const onTokenSaved = useCallback(() => {
    setHasToken(true);
    setShowModal(false);
  }, []);

  return { hasToken, showModal, setShowModal, onTokenSaved };
}
